import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { QuizLayout, StepNavigation } from '../../components/quiz';
import { AuthModal } from '../../components/shared';
import TrustStep from './steps/TrustStep';
import GoalsStep from './steps/GoalsStep';
import AgeStep from './steps/AgeStep';
import LifeStatusStep from './steps/LifeStatusStep';
import IncomeStep from './steps/IncomeStep';
import DebtStep from './steps/DebtStep';
import { quizToApiRequest } from '../../utils/quizToApi';
import { track, EVENTS } from '../../utils/analytics';
import type { SelectableCardOption, ConsumerQuizAnswers } from '../../types';

const CONSUMER_GOALS: SelectableCardOption[] = [
  { id: 'save_emergency', icon: '🛡️', title: 'I want to stop worrying about money', description: 'Build a safety net and stop living on the edge' },
  { id: 'invest', icon: '📈', title: 'I want my money working while I sleep', description: 'Start investing automatically' },
  { id: 'pay_debt', icon: '💳', title: "I'm ready to crush my debt", description: 'Pay off what I owe faster' },
  { id: 'retirement', icon: '🏖️', title: 'I want to retire comfortably', description: 'Build long-term wealth' },
  { id: 'buy_home', icon: '🏠', title: "I'm saving for a home", description: 'Reach my down payment goal' },
  { id: 'budget', icon: '😌', title: 'I want to spend without the guilt', description: 'Enjoy life knowing your bases are covered' },
  { id: 'family', icon: '👨‍👩‍👧‍👦', title: 'I want to provide for my family', description: 'Build stability for the people who matter' },
  { id: 'travel', icon: '✈️', title: 'I want to save for my dream trip', description: 'Set aside money for trips automatically' },
  { id: 'other', icon: '✏️', title: 'Other', description: 'You tell us, we\'ll build it' },
];

const TOTAL_STEPS = 6;

export default function ConsumerQuiz() {
  const navigate = useNavigate();
  const { state, updateQuizAnswers, setApiRequest } = useApp();
  const [currentStep, setCurrentStep] = useState(1);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const answers = state.quizAnswers as Partial<ConsumerQuizAnswers>;

  function trackCurrentStep() {
    switch (currentStep) {
      case 1:
        track(EVENTS.TRUST_PAGE_VIEWED, { userType: 'INDIVIDUAL' });
        break;
      case 2:
        track(EVENTS.QUIZ_GOAL_DONE, { goals: answers.goals, otherGoalText: answers.otherGoalText });
        break;
      case 3:
        track(EVENTS.QUIZ_AGE_DONE, { ageGroup: answers.ageGroup });
        break;
      case 4:
        track(EVENTS.QUIZ_LIFE_STATUS_DONE, { relationshipStatus: answers.relationshipStatus, dependentsStatus: answers.dependentsStatus });
        break;
      case 5:
        track(EVENTS.QUIZ_INCOME_DONE, { monthlyIncome: answers.monthlyIncome });
        break;
      case 6:
        track(EVENTS.QUIZ_DEBT_DONE, { hasDebt: answers.hasDebt, debtTypes: answers.debtTypes });
        break;
    }
  }

  function goNext() {
    trackCurrentStep();

    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((prev) => prev + 1);
    } else {
      track(EVENTS.QUIZ_COMPLETED, { userType: 'INDIVIDUAL', ...answers });

      // Build API request and show auth modal
      const apiRequest = quizToApiRequest({
        ...answers,
        userType: 'INDIVIDUAL',
      });
      if (apiRequest) {
        setApiRequest(apiRequest);
        setShowAuthModal(true);
      }
    }
  }

  function goBack() {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    } else {
      navigate('/quiz');
    }
  }

  function canContinue(): boolean {
    switch (currentStep) {
      case 1:
        return true; // Trust step - always can continue
      case 2:
        return (answers.goals?.length ?? 0) > 0;
      case 3:
        return Boolean(answers.ageGroup);
      case 4:
        // Only one of relationship or dependents is required
        return Boolean(answers.relationshipStatus) || Boolean(answers.dependentsStatus);
      case 5:
        return Boolean(answers.monthlyIncome);
      case 6:
        return answers.hasDebt !== null && answers.hasDebt !== undefined;
      default:
        return true;
    }
  }

  function renderStep() {
    switch (currentStep) {
      case 1:
        return <TrustStep userType="INDIVIDUAL" />;
      case 2:
        return (
          <GoalsStep
            goals={answers.goals || []}
            onChange={(goals) => updateQuizAnswers({ goals })}
            options={CONSUMER_GOALS}
            otherGoalText={answers.otherGoalText || ''}
            onOtherGoalTextChange={(otherGoalText) => updateQuizAnswers({ otherGoalText })}
            question="What would change your financial life the most?"
          />
        );
      case 3:
        return (
          <AgeStep
            ageGroup={answers.ageGroup || ''}
            onChange={(ageGroup) => updateQuizAnswers({ ageGroup })}
          />
        );
      case 4:
        return (
          <LifeStatusStep
            relationshipStatus={answers.relationshipStatus || ''}
            dependentsStatus={answers.dependentsStatus || ''}
            onRelationshipChange={(relationshipStatus) => updateQuizAnswers({ relationshipStatus })}
            onDependentsChange={(dependentsStatus) => updateQuizAnswers({ dependentsStatus })}
          />
        );
      case 5:
        return (
          <IncomeStep
            monthlyIncome={answers.monthlyIncome || ''}
            onChange={(monthlyIncome) => updateQuizAnswers({ monthlyIncome })}
          />
        );
      case 6:
        return (
          <DebtStep
            hasDebt={answers.hasDebt ?? null}
            debtTypes={answers.debtTypes || []}
            isBusinessDebt={false}
            onHasDebtChange={(hasDebt) => updateQuizAnswers({ hasDebt })}
            onDebtTypesChange={(debtTypes) => updateQuizAnswers({ debtTypes })}
          />
        );
      default:
        return null;
    }
  }

  return (
    <>
      <QuizLayout
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS}
        navigation={
          <StepNavigation
            onBack={goBack}
            onContinue={goNext}
            canContinue={canContinue()}
            continueLabel={currentStep === TOTAL_STEPS ? 'Build My Plan Now' : 'Continue'}
          />
        }
      >
        {renderStep()}
      </QuizLayout>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  );
}
